import { useContext, useEffect, useState } from "react";
import { UserStore } from "../..";
import BreadCrumbs from "../../components/breadcrumsb";
import { PageContainer } from "../../components/page";
import { PEProgressBar } from "../../components/linear-progress";
import {
  deleteAppBanner,
  getAppBanners,
  updateAppBanner,
  updateMobileAppBannerTimer,
} from "../../api/new-api";
import { runAuthHandler, transformAttractorData } from "../util";
import { useNavigate } from "react-router";
import MobileAttractorsContainer from "../mobile-attractor/MobileAttractorsContainer";
import { observer } from "mobx-react";
import { CloudArrowUpIcon } from "@heroicons/react/24/solid";
import { isEqual } from "lodash";
import SimpleSecondsContainer from "../../components/SimpleSecondsContainer";
import { PhonePreview } from "../../components/PhonePreview";
import {
  MobileBanner,
  MobileBannerResponse,
  UpdateMobileBannerRequest,
} from "../../types";

const fetchData = async ({
  setData,
  setLoading,
  setError,
  setAppBannerUpdateIntervalSecs,
}: {
  setData: (data: MobileBanner[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  setAppBannerUpdateIntervalSecs: (secs: number) => void;
}) => {
  setError("");
  try {
    const res = await getAppBanners();
    if (res.data) {
      setAppBannerUpdateIntervalSecs(
        res.data.data.bannerUpdateIntervalSecs || 0
      );
      setData(res.data.data.banners || []);
    } else {
      setData([]);
    }
  } catch (error) {
    setError("Error getting mobile banners");
  } finally {
    setLoading(false);
  }
};

const PAGE_TITLE = "Mobile Banners Page";
const MobileBannersPage = observer(() => {
  const userStore = useContext(UserStore);
  const navigate = useNavigate();
  const { selectedVenue } = userStore;

  const [loading, setLoading] = useState<boolean>(true);
  const [operationLoading, setOperationLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [data, setData] = useState<MobileBanner[]>([]);
  const [showingTodayAttractors, setShowingTodayAttractors] = useState<any[]>();
  const [filteredData, setFilteredData] = useState<MobileBanner[]>([]);
  // Modified activeFilter to include "upcoming"
  const [activeFilter, setActiveFilter] = useState<
    "week" | "all" | "expired" | "upcoming"
  >("week");
  const [appBannerUpdateIntervalSecs, setAppBannerUpdateIntervalSecs] =
    useState<number>(0);
  const [
    appBannerUpdateIntervalSecsEditable,
    setAppBannerUpdateIntervalSecsEditable,
  ] = useState<boolean>(false);
  const [intervalSeconds, setIntervalSeconds] = useState<number>(0);
  const [weekCount, setWeekCount] = useState<number>(0);
  const [allCount, setAllCount] = useState<number>(0);
  const [expiredCount, setExpiredCount] = useState<number>(0);
  const [todayCount, setTodayCount] = useState<number>(0);
  // New state for upcoming banners count
  const [upcomingCount, setUpcomingCount] = useState<number>(0);

  const resetData = () => {
    setData([]);
    setShowingTodayAttractors([]);
    setFilteredData([]);
    setWeekCount(0);
    setAllCount(0);
    setExpiredCount(0);
    setTodayCount(0);
    setUpcomingCount(0); // Reset upcoming count
  };

  const getBanners = async () => {
    fetchData({
      setData,
      setLoading,
      setError,
      setAppBannerUpdateIntervalSecs,
    });
  };

  const onUpdate = async (banner: MobileBannerResponse) => {
    setOperationLoading(true);
    try {
      const updateData: UpdateMobileBannerRequest = {
        appBannerId: banner.mobileBannerId,
        displayLinkUrl: banner.displayLinkUrl,
        startDateEpoch: banner.startDateEpoch,
        endDateEpoch: banner.endDateEpoch,
        dayVisibility: banner.dayVisibility,
      };

      await updateAppBanner(updateData);
      await getBanners();
    } catch (error) {
      console.error("Error updating mobile banner", error);
    } finally {
      setOperationLoading(false);
    }
  };

  const onDelete = async (id: string | number) => {
    setOperationLoading(true);
    try {
      await deleteAppBanner({ appBannerId: id });
      await getBanners();
    } catch (error) {
      console.error("Error deleting mobile banner", error);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleSaveIntervalSeconds = async () => {
    if (intervalSeconds < 3) {
      alert("Interval minimum is 3 seconds");
      return;
    }
    if (isEqual(appBannerUpdateIntervalSecs, intervalSeconds)) return;

    try {
      const res = await updateMobileAppBannerTimer(intervalSeconds);
      if (res.status === 200) {
        alert("Successfully updated interval seconds");
        setAppBannerUpdateIntervalSecs(intervalSeconds);
        setAppBannerUpdateIntervalSecsEditable(false);
      }
    } catch (err) {
      console.log(err);
      setAppBannerUpdateIntervalSecsEditable(false);
      setIntervalSeconds(appBannerUpdateIntervalSecs);
    }
  };

  const filterBanners = () => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const endOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999
    );

    // Helper function to check if a banner is active during a time period
    const isBannerActiveInPeriod = (
      banner: MobileBanner,
      periodStart: Date,
      periodEnd: Date
    ) => {
      const startDate = banner.startDateEpoch
        ? new Date(banner.startDateEpoch)
        : null;
      const endDate = banner.endDateEpoch
        ? new Date(banner.endDateEpoch)
        : null;

      // If no start date, consider it as active from the beginning
      if (!startDate) return false;

      // If no end date, consider it as active indefinitely
      if (!endDate) {
        return startDate <= periodEnd;
      }

      // Check if the banner's active period overlaps with the given period
      return startDate <= periodEnd && endDate >= periodStart;
    };

    const weekBanners = data.filter((banner) => {
      const currentTime = Date.now();
      const currentDayOfWeek = new Date()
        .toLocaleDateString("en-US", { weekday: "short" })
        .toUpperCase(); // MON, TUE, etc.

      return (
        banner.dayVisibility != undefined &&
        banner.startDateEpoch != undefined &&
        banner.endDateEpoch != undefined &&
        banner.dayVisibility.includes(currentDayOfWeek) &&
        isBannerActiveInPeriod(banner, weekAgo, today)
      );
    });

    const todayBanners = data.filter((banner) => {
      const currentTime = Date.now();
      const currentDayOfWeek = new Date()
        .toLocaleDateString("en-US", { weekday: "short" })
        .toUpperCase(); // MON, TUE, etc.

      return (
        banner.dayVisibility != undefined &&
        banner.startDateEpoch != undefined &&
        banner.endDateEpoch != undefined &&
        banner.dayVisibility.includes(currentDayOfWeek) &&
        currentTime >= banner.startDateEpoch &&
        currentTime <= banner.endDateEpoch
      );
    });

    const expiredBanners = data.filter((banner) => {
      const endDate = banner.endDateEpoch
        ? new Date(banner.endDateEpoch)
        : null;
      return endDate && endDate < today;
    });

    // New filter for upcoming banners
    const upcomingBanners = data.filter((banner) => {
      const startDate = banner.startDateEpoch
        ? new Date(banner.startDateEpoch)
        : null;
      return startDate && startDate > today;
    });

    setShowingTodayAttractors(todayBanners);
    setWeekCount(weekBanners.length);
    setAllCount(data.length);
    setExpiredCount(expiredBanners.length);
    setTodayCount(todayBanners.length);
    setUpcomingCount(upcomingBanners.length); // Set upcoming count

    if (activeFilter === "week") {
      setFilteredData(weekBanners);
    } else if (activeFilter === "all") {
      setFilteredData(data);
    } else if (activeFilter === "expired") {
      setFilteredData(expiredBanners);
    } else if (activeFilter === "upcoming") {
      setFilteredData(upcomingBanners);
    }
  };

  useEffect(() => {
    transformAttractorData({
      data,
      setCurrentAttractors: setShowingTodayAttractors,
    });
    filterBanners();
  }, [data, activeFilter]);

  useEffect(() => {
    resetData();
    runAuthHandler({ userStore, navigate });
    getBanners();
  }, [selectedVenue]);

  useEffect(() => {
    setIntervalSeconds(appBannerUpdateIntervalSecs);
  }, [appBannerUpdateIntervalSecs]);

  return (
    <PageContainer>
      {/* Operation Loading Overlay */}
      {operationLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-8 shadow-xl">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <p className="text-lg font-medium text-gray-700">Processing...</p>
            </div>
          </div>
        </div>
      )}

      <BreadCrumbs
        routes={[
          { path: "/home", breadcrumbName: "Home" },
          { path: "/mobile-banners", breadcrumbName: "Mobile Banners" },
        ]}
      />
      <div className="text-3xl mb-6 text-center font-bold">{PAGE_TITLE}</div>

      {loading && <PEProgressBar />}

      {!loading && (
        <div className="my-5">
          <div className="flex bg-slate-100 p-10 h-[410px] justify-center gap-20">
            <div className="flex overflow-hidden">
              <PhonePreview
                myHomeAppBanner={
                  (data.length > 0
                    ? {
                        fullscreenS3Key: data[0].fullscreenS3Key,
                        thumbnailS3Key: data[0].thumbnailS3Key,
                        externalLinkUrl: data[0].externalLinkUrl,
                      }
                    : {
                        fullscreenS3Key: "",
                        thumbnailS3Key: "",
                        externalLinkUrl: "",
                      }) as MobileBanner
                }
                screenType="MY_HOME"
              />
            </div>

            <div
              onClick={() => navigate("/mobile-banners/create")}
              className="hover:opacity-75 hover:cursor-pointer flex-cen-stack border-4 p-10 text-center"
            >
              <div className="flex flex-col items-center gap-1">
                <p className="text-md font-bold">
                  Click here to start adding a Mobile Banner
                </p>
                <p className="text-md font-light">upload specs:</p>
                <p className="text-md font-light">
                  Thumbnail: Width to Height Ratio of 6:1
                </p>
                <p className="text-md font-light">
                  Fullscreen: Width to Height ratio of 9:16
                </p>
                <CloudArrowUpIcon className="h-10 w-10 text-blue-500" />
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && (
        <SimpleSecondsContainer
          label="Banner Changing Interval Seconds"
          initialValue={appBannerUpdateIntervalSecs}
          value={intervalSeconds}
          setValue={setIntervalSeconds}
          editable={appBannerUpdateIntervalSecsEditable}
          onEdit={setAppBannerUpdateIntervalSecsEditable}
          onSave={handleSaveIntervalSeconds}
          onCancel={() => {
            setAppBannerUpdateIntervalSecsEditable(false);
            setIntervalSeconds(appBannerUpdateIntervalSecs);
          }}
        />
      )}

      {!loading && (
        <div className="w-full">
          <div className="block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
            <h5 className="text-center mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              Showing Today
            </h5>
            <p className="text-center font-normal text-gray-700 dark:text-gray-400">
              Here are the current app banners:
            </p>

            <div className="mt-4">
              <MobileAttractorsContainer
                errorMessage="No current app banners found"
                data={showingTodayAttractors as any}
                getAttractors={getBanners}
                onUpdate={onUpdate}
                onDelete={onDelete}
                objectKey="mobileBannerId"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-center gap-4 mb-6">
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "week"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("week")}
            >
              Showing This Week ({weekCount})
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "all"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("all")}
            >
              All Banners ({allCount})
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "expired"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("expired")}
            >
              Expired Banners ({expiredCount})
            </button>
            {/* New Upcoming Button */}
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "upcoming"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("upcoming")}
            >
              Upcoming Banners ({upcomingCount})
            </button>
          </div>

          <div className="block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
            <h5 className="text-center mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              {activeFilter === "week"
                ? "Showing This Week"
                : activeFilter === "all"
                ? "All Banners"
                : activeFilter === "expired"
                ? "Expired Banners"
                : activeFilter === "upcoming"
                ? "Upcoming Banners"
                : "Showing Today"}
            </h5>
            <p className="text-center font-normal text-gray-700 dark:text-gray-400">
              {activeFilter === "week"
                ? "Banners from the last 7 days"
                : activeFilter === "all"
                ? "All showing and finished banners"
                : activeFilter === "expired"
                ? "Finished banners"
                : activeFilter === "upcoming"
                ? "Banners scheduled to start in the future"
                : "Banners from today"}
            </p>

            <div className="mt-4">
              <MobileAttractorsContainer
                errorMessage={`No ${
                  activeFilter === "week"
                    ? "current"
                    : activeFilter === "all"
                    ? "other"
                    : activeFilter === "expired"
                    ? "expired"
                    : activeFilter === "upcoming"
                    ? "upcoming"
                    : "today's"
                } banners found`}
                data={filteredData as any}
                getAttractors={getBanners}
                onUpdate={onUpdate}
                onDelete={onDelete}
                objectKey="mobileBannerId"
              />
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
});

export default MobileBannersPage;
